import { DungeonAutomap } from '../dungeonAutomap';
import {
  initDungeonCombatHud,
  type DungeonCombatHudHandle,
} from '../dungeonCombatHud';
import { initDungeonMapUi, type DungeonMapUi } from '../dungeonMapUi';
import type {
  DungeonHeroPanelDeps,
  DungeonHeroPanelHandle,
  initDungeonHeroPanel,
} from '../dungeonHeroPanel';
import {
  dungeonSourceKey,
  type DungeonBlueprint,
} from '../sim/dungeon';
import {
  deltaForDirection,
  nearestDirectionForDelta,
} from '../sim/direction';
import type { EntityId } from '../sim/entity';
import {
  ability as catalogAbility,
  abilityPresentation,
  type AbilityDefinition,
  type AbilityId,
  type AbilityShape,
} from '../sim/combat/abilityCatalog';
import { planDungeonCharge } from '../sim/combat/abilityShapes';
import type { Soulstone } from '../sim/arpg/soulstone';
import { heroSlotAbilities } from '../dungeonCombatHud';
// The class registry rather than `hero.ts`: asking whether a string names a
// playable class is a question about the roster, and routing it through the
// Hero module would put Hero validation, the derivation fold, and the whole
// item catalog in the cold entry to answer it.
import {
  isPlayableArpgClassId as isPlayableHeroClassId,
  type ArpgAbilitySlot,
} from '../sim/arpg/classes';
import {
  COMBAT_LAB_SCENARIO_IDS,
  COMBAT_LAB_SCENARIO_LIST,
  type CombatLabScenarioId,
} from '../sim/combatLab';
import { whiffCue } from '../audio/combatCue';
import type { FeatureContext } from './featureContext';

const COMBAT_LAB_USAGE =
  `Combat Lab scenarios: ${COMBAT_LAB_SCENARIO_LIST}.`;

/**
 * One ability the client predicts, resolved from the catalog by id.
 *
 * The wire carries ids alone. Mechanics come from the definition and clips from
 * the presentation descriptor, which is what this edge exists to translate, so
 * a retuned range or a swapped clip needs no protocol change.
 */
interface PredictedAbility {
  id: AbilityId;
  manaCost: number;
  definition: AbilityDefinition;
}

function predictedAbility(
  msg: { active: boolean; abilities?: readonly AbilityId[] },
  id: AbilityId,
): PredictedAbility | undefined {
  if (!msg.active || !msg.abilities?.includes(id)) return undefined;
  const definition = catalogAbility(id);
  return definition
    ? { id, manaCost: definition.costs.mana, definition }
    : undefined;
}

/** The fixed clip an ability's presentation declares, if it declares one. */
function fixedClip(id: AbilityId): string | undefined {
  const clip = abilityPresentation(id)?.clip;
  return clip?.kind === 'fixed' ? clip.clipId : undefined;
}

/** The clip an ability's presentation selects for this weapon grip. */
function grippedClip(
  id: AbilityId,
  grip: 'one-handed' | 'two-handed',
): string | undefined {
  const clip = abilityPresentation(id)?.clip;
  if (!clip) return undefined;
  return clip.kind === 'grip'
    ? grip === 'two-handed'
      ? clip.twoHanded
      : clip.oneHanded
    : clip.clipId;
}

function shapeOf<K extends AbilityShape['kind']>(
  ability: PredictedAbility | undefined,
  kind: K,
): Extract<AbilityShape, { kind: K }> | undefined {
  const shape = ability?.definition.shape;
  return shape?.kind === kind
    ? (shape as Extract<AbilityShape, { kind: K }>)
    : undefined;
}

/** How long an ability's status payload runs, in ticks. */
function statusDurationTicks(ability: PredictedAbility | undefined): number {
  const payload = ability?.definition.payloads.find(
    (entry) => entry.kind === 'applyStatus',
  );
  return payload?.kind === 'applyStatus' ? payload.durationTicks : 0;
}

export interface DungeonInstallDeps {
  initMapUi?: (doc: Document) => DungeonMapUi;
  initCombatHud?: (doc: Document) => DungeonCombatHudHandle;
  initHeroPanel?: (
    deps: Parameters<typeof initDungeonHeroPanel>[0],
  ) => DungeonHeroPanelHandle;
}

/**
 * The Hero panel, loaded the first time a player walks up to a dungeon gate.
 *
 * The panel is the largest piece of dungeon UI and, since itemization landed
 * (PRD #795), it is also what drags the whole item catalog in behind it: bases,
 * the material ladder, the affix vocabulary, tooltips, and comparison. Every
 * player pays for the production entry script before they have opened anything,
 * and most of them never walk to the gate, so none of that belongs in the cold
 * entry. It is a separate chunk fetched on the interaction that needs it.
 *
 * Laziness is invisible to the caller. `open` and `update` are recorded while
 * the chunk is in flight and replayed against the real handle when it arrives,
 * so a roster message that races the fetch is rendered rather than dropped, and
 * a panel closed before the chunk lands stays closed.
 */
function lazyHeroPanel(
  load: () => Promise<DungeonHeroPanelHandle>,
): DungeonHeroPanelHandle {
  let handle: DungeonHeroPanelHandle | undefined;
  let loading = false;
  let wantsOpen = false;
  let pending: Parameters<DungeonHeroPanelHandle['update']>[0] | undefined;
  const ensure = (): void => {
    if (handle || loading) return;
    loading = true;
    void load().then((loaded) => {
      handle = loaded;
      loading = false;
      if (wantsOpen) loaded.open();
      if (pending) loaded.update(pending);
      pending = undefined;
    });
  };
  return {
    open: () => {
      wantsOpen = true;
      if (handle) handle.open();
      else ensure();
    },
    close: () => {
      wantsOpen = false;
      handle?.close();
    },
    update: (message) => {
      if (handle) handle.update(message);
      else {
        pending = message;
        ensure();
      }
    },
  };
}

/** Install exact-seed dungeon entry and the run-local semantic fog-of-war map. */
export function installDungeon(ctx: FeatureContext, deps: DungeonInstallDeps = {}): void {
  const ui = (deps.initMapUi ?? initDungeonMapUi)(ctx.ui);
  const combatHud = (deps.initCombatHud ?? initDungeonCombatHud)(ctx.ui);
  // The latest roster the server sent, so the dev chat commands can resolve a
  // class name to an owned Soulstone id without a second round trip.
  let knownSoulstones: Soulstone[] = [];
  const heroPanelDeps: DungeonHeroPanelDeps = {
    document: ctx.ui,
    onRequest: () => ctx.client()?.requestSoulstones(),
    onAttune: (classId) => ctx.client()?.attuneSoulstone(classId),
    onEquip: (soulstoneId, itemId) => ctx.client()?.equipHeroItem(soulstoneId, itemId),
    onUnequip: (soulstoneId, slot) => ctx.client()?.unequipHeroItem(soulstoneId, slot),
    onAllocate: (soulstoneId, stats) => ctx.client()?.allocateHeroStats(soulstoneId, stats),
    onRespec: (soulstoneId) => ctx.client()?.respecHero(soulstoneId),
    // The Bone Knight Wall dialog's own actions (PRD #837 slices 3 and 7):
    // taking up selects whose campaign the far exit resolves and at which
    // unlocked tier, wiping destroys a stone (the panel confirmed first), and
    // travel re-enters an unlocked destination at the tier it was stamped
    // with. The tier is opaque data from the roster; nothing here reads it.
    onTakeUp: (soulstoneId, tier) =>
      ctx.client()?.takeUpSoulstone(soulstoneId, tier),
    onWipe: (soulstoneId) => ctx.client()?.wipeSoulstone(soulstoneId),
    onTravel: (soulstoneId, campaignNodeId, tier) => {
      const playerId = ctx.playerId();
      if (playerId === undefined) return;
      ctx.client()?.sendCommand({
        kind: 'StartDungeonRun',
        entity: playerId,
        soulstoneId,
        campaignNodeId,
        campaignTier: tier,
      });
      heroPanel.close();
    },
  };
  // A test that injects a panel gets it synchronously and never fetches a
  // chunk; a player gets the chunk on the interaction that needs it.
  const heroPanel: DungeonHeroPanelHandle = deps.initHeroPanel
    ? deps.initHeroPanel(heroPanelDeps)
    : lazyHeroPanel(() =>
        import('../dungeonHeroPanel').then((module) =>
          module.initDungeonHeroPanel(heroPanelDeps),
        ),
      );
  let blueprint: DungeonBlueprint | undefined;
  let automap: DungeonAutomap | undefined;
  let runKey = '';
  let wasInDungeon = false;
  let active = false;
  /**
   * Which entities are dungeon monsters, read from the replicated world (PRD
   * #794).
   *
   * A floor holds a couple of hundred monsters now, so the run status cannot
   * carry the list and the client does not need it to: every monster rides the
   * snapshot with its `dungeonEnemy` marker already. Reading it here is what
   * keeps hit feedback, death shakes, and the Charge preview working against
   * whatever the player actually swung at rather than only against the one
   * target the status names.
   */
  const isDungeonMonster = (id: EntityId): boolean =>
    active && ctx.world()?.get(id)?.dungeonEnemy === true;
  const dungeonMonsterIds = (): EntityId[] =>
    (ctx.world()?.all() ?? [])
      .filter((entity) => entity.dungeonEnemy === true)
      .map((entity) => entity.id);
  /**
   * What each action-bar slot casts right now.
   *
   * This replaced five named Warrior fields (PRD #800). The class declaration
   * authors which ability answers which finger, the run's loadout says which of
   * them are unlocked, and everything the input layer needs after that - the
   * clip, the geometry it previews, whether it winds up - comes from the
   * catalog entry by id. So the Archer and the Mage bind with no edit here, and
   * the shipped Warrior bindings are exactly the ones it shipped with.
   */
  let bound: Partial<Record<ArpgAbilitySlot, PredictedAbility>> = {};
  /** The slot holding a dash, which is the one slot that arms before it fires. */
  let dashSlot: ArpgAbilitySlot | undefined;
  let dashAvailable = false;
  let currentMainHandGrip: 'one-handed' | 'two-handed' = 'two-handed';
  const predictedBuffActionIds = new Set<string>();
  const predictedBuffBySeq = new Map<number, string>();
  let castSeq = 1;
  let runGeneration = 0;
  /** Slots being held down, which re-cast as soon as the cooldown allows. */
  const heldSlots = new Set<ArpgAbilitySlot>();
  let chargeTargeting = false;
  /**
   * When a predicted wind-up is expected to be over, in client milliseconds.
   *
   * Prediction only: the server refuses a second cast while one is in flight,
   * and this stops the client asking for one it knows will be refused, which
   * would spend predicted mana and then reconcile it back a tick later.
   */
  let localBusyUntilMs = 0;
  let attackCursor: { clientX: number; clientY: number } | undefined;
  let lastPointerAim: { x: number; y: number } | undefined;
  let lastPointerCursor: { clientX: number; clientY: number } | undefined;
  let labHelpTimer: number | undefined;

  const hideCombatLabHelp = (): void => {
    if (labHelpTimer !== undefined) {
      ctx.ui.defaultView?.clearTimeout(labHelpTimer);
      labHelpTimer = undefined;
    }
    ctx.ui.getElementById('dungeon-lab-command-help')?.remove();
  };

  const showCombatLabHelp = (): void => {
    hideCombatLabHelp();
    const notice = ctx.ui.createElement('div');
    notice.id = 'dungeon-lab-command-help';
    notice.className = 'dungeon-lab-command-help';
    notice.setAttribute('role', 'status');
    notice.textContent = COMBAT_LAB_USAGE;
    ctx.ui.body.append(notice);
    labHelpTimer = ctx.ui.defaultView?.setTimeout(
      hideCombatLabHelp,
      6_000,
    );
  };

  /** Warm every clip the bound kit can play, whichever class is holding it. */
  const prepareBoundAbilities = (): void => {
    const playerId = ctx.playerId();
    const renderer = ctx.renderer();
    if (!active || playerId === undefined || !renderer) return;
    for (const ability of Object.values(bound)) {
      const clip = grippedClip(ability.id, currentMainHandGrip);
      if (clip) void renderer.prepareActionAnimation(playerId, clip);
    }
  };

  ctx.events.on('dungeonRunStatus', (msg) => {
    if (msg.active && !active) runGeneration += 1;
    active = msg.active;
    const world = ctx.world();
    if (active && world && msg.movement) {
      ctx.state.observeDungeonMovement(msg.movement);
    }
    if (!active) {
      heldSlots.clear();
      chargeTargeting = false;
      ctx.renderer()?.clearDungeonChargePreview?.();
      attackCursor = undefined;
      predictedBuffActionIds.clear();
      predictedBuffBySeq.clear();
      localBusyUntilMs = 0;
    }
    currentMainHandGrip = msg.mainHandGrip ?? 'two-handed';
    // Layout comes from the class declaration and membership from the run's
    // own loadout, so an ability the Hero has not unlocked binds to nothing.
    const slotted = heroSlotAbilities(msg.classId, msg.abilities ?? [msg.ability]);
    bound = {};
    dashSlot = undefined;
    for (const [slot, abilityId] of Object.entries(slotted)) {
      const ability = predictedAbility(msg, abilityId);
      if (!ability) continue;
      bound[slot as ArpgAbilitySlot] = ability;
      if (ability.definition.shape.kind === 'dash') {
        dashSlot = slot as ArpgAbilitySlot;
      }
    }
    const dash = dashSlot === undefined ? undefined : bound[dashSlot];
    dashAvailable = Boolean(
      dash &&
        (msg.cooldowns[dash.id] ?? 0) === 0 &&
        msg.mana >= dash.manaCost &&
        msg.hp > 0,
    );
    if (chargeTargeting && !dashAvailable) {
      chargeTargeting = false;
      ctx.renderer()?.clearDungeonChargePreview?.();
    }
    combatHud.update(msg);
    ctx.state.setHudSuppressed(msg.active);
    prepareBoundAbilities();
  });

  // One announced area from a telegraphed monster cast (PRD #793). The whole
  // point of a telegraph is that the player can see and answer it, so the exact
  // footprint is drawn on the ground for the declared warning and disappears as
  // the damage lands. Ephemeral: a dropped message costs a warning and nothing
  // else, and the UI being hidden means nobody is looking at the ground anyway.
  ctx.events.on('dungeonTelegraph', (msg) => {
    if (ctx.ui.hidden) return;
    ctx.renderer()?.showDungeonTelegraph?.(
      msg.shape,
      { origin: msg.origin, ...(msg.point ? { point: msg.point } : {}) },
      msg.ticksUntil,
    );
  });

  ctx.events.on('dungeonCastResult', (msg) => {
    combatHud.reconcile(msg);
    if (!msg.accepted) localBusyUntilMs = 0;
    const actionId = predictedBuffBySeq.get(msg.seq);
    if (!actionId) return;
    predictedBuffBySeq.delete(msg.seq);
    if (msg.accepted) {
      ctx.audio.play({ sound: 'warrior-bless', volume: 1 });
      return;
    }
    predictedBuffActionIds.delete(actionId);
    ctx.renderer()?.cancelDungeonAegis(actionId);
  });
  ctx.events.on('soulstoneRoster', (msg) => {
    knownSoulstones = msg.soulstones;
    heroPanel.update(msg);
  });

  // The Soulstone dialog belongs to the Bone Knight Wall's keeper (PRD #837
  // slice 3): the panel-at-the-town-gate flow retired with the instant
  // procedural run. Both affordances key off the replicated `soulstoneKeeper`
  // marker, the escort pattern, so a generated name proves nothing.
  const openSoulstoneDialog = (): void => {
    heroPanel.open();
  };
  ctx.menu.addEntityMenu((target) => {
    if (target.entity.soulstoneKeeper !== true) return null;
    return {
      items: [{ label: 'Speak with the keeper', onSelect: openSoulstoneDialog }],
      exclusive: true,
    };
  });

  ctx.events.on('actionAnimation', (msg) => {
    // A defensive buff reads as a glow for as long as it runs, and which clip
    // announces it is the ability's own business: the bound self-shaped slot
    // says what to watch for, so Aegis and Ward light up the same way.
    const buff = boundBuff();
    const buffClip = buff && fixedClip(buff.id);
    if (
      active &&
      msg.entityId === ctx.playerId() &&
      buff &&
      buffClip !== undefined &&
      msg.clip === buffClip
    ) {
      if (msg.actionId && predictedBuffActionIds.delete(msg.actionId)) return;
      ctx.renderer()?.playDungeonAegis(
        msg.entityId,
        statusDurationTicks(buff) * 50,
      );
      ctx.audio.play({ sound: 'warrior-bless', volume: 1 });
      return;
    }
    if (!active || !isDungeonMonster(msg.entityId) || msg.clip !== 'die') return;
    ctx.renderer()?.kickShake(4);
    ctx.renderer()?.burstAtEntity(msg.entityId, 0xd7b85c, 18);
  });

  ctx.events.on('combatText', (msg) => {
    if (!active || msg.outcome !== 'damage') return;
    if (isDungeonMonster(msg.entityId)) {
      ctx.renderer()?.kickShake(2);
      ctx.renderer()?.burstAtEntity(msg.entityId, 0xffd36a, 12);
      ctx.audio.play({ sound: 'halberd-hit', volume: 1 });
      return;
    }
    if (msg.entityId === ctx.playerId()) {
      ctx.renderer()?.kickShake(3);
    }
  });

  const boundedAim = (
    target: { x: number; y: number },
  ): { x: number; y: number } | undefined => {
    const world = ctx.world();
    if (
      !world ||
      !Number.isFinite(target.x) ||
      !Number.isFinite(target.y)
    ) {
      return undefined;
    }
    return {
      x: Math.max(0, Math.min(world.map.width - 1, target.x)),
      y: Math.max(0, Math.min(world.map.height - 1, target.y)),
    };
  };

  const pointerGroundAim = (
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | undefined => {
    const renderer = ctx.renderer();
    if (!renderer) return undefined;
    const rect = ctx.canvas.getBoundingClientRect();
    const target = renderer.screenToGroundTile(
      clientX - rect.left,
      clientY - rect.top,
    );
    return boundedAim(target);
  };

  const pointerAim = (
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | undefined => {
    return pointerGroundAim(clientX, clientY);
  };

  /** The bound slot whose ability buffs the caster, if the class carries one. */
  const boundBuff = (): PredictedAbility | undefined =>
    Object.values(bound).find(
      (ability) => ability.definition.shape.kind === 'self',
    );

  /** Whether a predicted wind-up is still running, so a second cast is refused. */
  const locallyBusy = (): boolean =>
    localBusyUntilMs > (ctx.ui.defaultView?.performance?.now() ?? 0);

  /**
   * Where a cast aims: the cursor, or one tile ahead when the cursor is on the
   * caster's own feet, which is the fallback every Warrior path already used.
   */
  const aimFor = (
    origin: { x: number; y: number; facing: number },
    cursorAim: { x: number; y: number } | undefined,
  ): { x: number; y: number } | undefined => {
    const fallback = deltaForDirection(origin.facing);
    return boundedAim(
      !cursorAim ||
        Math.hypot(cursorAim.x - origin.x, cursorAim.y - origin.y) < 0.01
        ? { x: origin.x + fallback.dx, y: origin.y + fallback.dy }
        : cursorAim,
    );
  };

  /**
   * The local read of a cast, chosen by delivery shape rather than by ability.
   *
   * Every preview here answers a mechanical property of the catalog entry: a
   * cone sweeps its own arc, a lane draws its own length, a self buff glows for
   * its own status duration. A shot and a placed trap deliberately draw nothing
   * locally - what a projectile does after it leaves is the server's to say,
   * and predicting a flight the server owns is how a client lies (PRD #800).
   */
  const previewCast = (
    ability: PredictedAbility,
    playerId: EntityId,
    origin: { x: number; y: number },
    facing: number,
    aim: { x: number; y: number },
    actionId: string,
  ): void => {
    const renderer = ctx.renderer();
    if (!renderer) return;
    // A wind-up is announced by the server for its whole lead, so predicting
    // the pose here would play it twice and start it early.
    if (ability.definition.castTicks > 0) return;
    const clip = grippedClip(ability.id, currentMainHandGrip);
    if (clip) {
      renderer.playActionAnimation(playerId, clip, facing, actionId);
    }
    const shape = ability.definition.shape;
    const towardAim = { x: aim.x - origin.x, y: aim.y - origin.y };
    if (shape.kind === 'cone') {
      renderer.playDungeonSweep?.(
        playerId,
        towardAim,
        shape.rangeTiles,
        shape.arcDegrees,
      );
      const cue = whiffCue(0.72);
      if (cue) ctx.audio.play(cue);
      return;
    }
    if (shape.kind === 'circle') {
      renderer.playDungeonWhirlwind(playerId, facing, shape.radiusTiles);
      const cue = whiffCue(0.88);
      if (cue) ctx.audio.play(cue);
      return;
    }
    if (shape.kind === 'line') {
      renderer.playDungeonPierce?.(
        playerId,
        towardAim,
        shape.rangeTiles,
        shape.widthTiles,
      );
      const cue = whiffCue(0.82);
      if (cue) ctx.audio.play(cue);
      return;
    }
    if (shape.kind === 'self') {
      predictedBuffActionIds.add(actionId);
      predictedBuffBySeq.set(castSeq, actionId);
      renderer.playDungeonAegis(
        playerId,
        statusDurationTicks(ability) * 50,
        actionId,
      );
    }
  };

  /**
   * Cast whatever is bound to one slot.
   *
   * One path for every ability the three classes carry. What differs between
   * them - the aim, the clip, the local read, the cost - is read from the
   * catalog entry, which is what lets a fourth class bind with no edit here.
   */
  const castSlot = (
    slot: ArpgAbilitySlot,
    cursor?: { clientX: number; clientY: number },
  ): void => {
    const ability = bound[slot];
    const playerId = ctx.playerId();
    const player = playerId === undefined ? undefined : ctx.world()?.get(playerId);
    if (!active || !ability || playerId === undefined || !player?.position) return;
    if (ability.definition.shape.kind === 'dash') {
      if (chargeTargeting) tryCharge();
      else armChargeTargeting();
      return;
    }
    if (locallyBusy()) return;
    const cursorAim = cursor
      ? pointerGroundAim(cursor.clientX, cursor.clientY)
      : lastPointerAim;
    const origin = player.position;
    const selfCast = ability.definition.shape.kind === 'self';
    const aim = selfCast
      ? { x: origin.x, y: origin.y }
      : aimFor(origin, cursorAim);
    if (!aim) return;
    const facing = selfCast
      ? origin.facing
      : nearestDirectionForDelta(aim.x - origin.x, aim.y - origin.y);
    if (facing === null) return;
    if (!combatHud.tryPredictCast(castSeq, ability.id)) return;
    const actionId = `dungeon-cast:${runGeneration}:${castSeq}`;
    if (!selfCast) ctx.state.faceCast(facing);
    previewCast(
      ability,
      playerId,
      { x: origin.x, y: origin.y },
      facing,
      aim,
      actionId,
    );
    const committedTicks =
      ability.definition.castTicks + ability.definition.recoveryTicks;
    if (committedTicks > 0) {
      localBusyUntilMs =
        (ctx.ui.defaultView?.performance?.now() ?? 0) + committedTicks * 50;
    }
    ctx.client()?.sendCommand({
      kind: 'CastDungeonAbility',
      entity: playerId,
      seq: castSeq,
      abilityId: ability.id,
      actionId,
      aim,
    });
    castSeq = castSeq === 0xffff ? 1 : castSeq + 1;
  };

  const plannedCharge = (): {
    ability: PredictedAbility;
    dash: Extract<AbilityShape, { kind: 'dash' }>;
    playerId: EntityId;
    aim: { x: number; y: number };
    plan: NonNullable<ReturnType<typeof planDungeonCharge>>;
  } | undefined => {
    const ability = dashSlot === undefined ? undefined : bound[dashSlot];
    const dash = shapeOf(ability, 'dash');
    const playerId = ctx.playerId();
    const world = ctx.world();
    const player = playerId === undefined ? undefined : world?.get(playerId);
    if (
      !active ||
      !world ||
      !ability ||
      !dash ||
      playerId === undefined ||
      !player?.position
    ) {
      return;
    }
    const origin = ctx.state.localMovementPose() ?? player.position;
    const cursorAim = lastPointerCursor
      ? pointerGroundAim(lastPointerCursor.clientX, lastPointerCursor.clientY)
      : lastPointerAim;
    const aim = aimFor(origin, cursorAim);
    if (!aim) return undefined;
    const plan = planDungeonCharge(
      world,
      playerId,
      dungeonMonsterIds(),
      aim,
      dash.distanceTiles,
      origin,
    );
    if (!plan) return undefined;
    return { ability, dash, playerId, aim, plan };
  };

  const updateChargePreview = (): void => {
    if (!chargeTargeting) return;
    const planned = plannedCharge();
    if (!planned) {
      ctx.renderer()?.clearDungeonChargePreview?.();
      return;
    }
    ctx.renderer()?.showDungeonChargePreview?.(
      planned.plan.start,
      planned.plan.end,
    );
  };

  const cancelChargeTargeting = (): void => {
    chargeTargeting = false;
    ctx.renderer()?.clearDungeonChargePreview?.();
  };

  const armChargeTargeting = (): void => {
    if (!active || dashSlot === undefined || !dashAvailable) return;
    chargeTargeting = true;
    updateChargePreview();
  };

  const tryCharge = (): void => {
    const planned = plannedCharge();
    if (!planned) return;
    const { ability, dash, playerId, aim, plan } = planned;
    if (!combatHud.tryPredictCast(castSeq, ability.id)) {
      cancelChargeTargeting();
      return;
    }
    cancelChargeTargeting();
    const actionId = `dungeon-cast:${runGeneration}:${castSeq}`;
    ctx.state.rushDungeon(plan.end, plan.facing, dash.durationMs);
    ctx.renderer()?.playDungeonCharge?.(
      playerId,
      plan.start,
      plan.end,
      plan.contacts,
      dash.durationMs,
    );
    const movement = ctx.state.dungeonMovementCheckpoint();
    ctx.client()?.sendCommand({
      kind: 'CastDungeonAbility',
      entity: playerId,
      seq: castSeq,
      abilityId: ability.id,
      actionId,
      aim,
      ...(movement ? { movement } : {}),
    });
    castSeq = castSeq === 0xffff ? 1 : castSeq + 1;
  };

  ctx.canvas.addEventListener(
    'pointerdown',
    (event) => {
      if (
        !active ||
        (event.button !== 0 && event.button !== 2) ||
        event.pointerType === 'touch'
      ) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      lastPointerCursor = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      const aim = pointerAim(event.clientX, event.clientY);
      lastPointerAim = aim;
      if (event.button === 0) {
        // The dash arms on its own slot and fires on the left button, which is
        // the Charge flow generalised: one aimed confirmation for a committed
        // traversal, whichever class owns it.
        if (chargeTargeting) {
          tryCharge();
          return;
        }
        heldSlots.add('left-mouse');
        castSlot('left-mouse', { clientX: event.clientX, clientY: event.clientY });
      } else {
        heldSlots.add('right-mouse');
        attackCursor = {
          clientX: event.clientX,
          clientY: event.clientY,
        };
        void aim;
        castSlot('right-mouse', { clientX: event.clientX, clientY: event.clientY });
      }
    },
    { capture: true },
  );
  ctx.canvas.addEventListener(
    'contextmenu',
    (event) => {
      if (!active) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true },
  );
  ctx.canvas.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') return;
    lastPointerCursor = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    lastPointerAim = pointerAim(event.clientX, event.clientY);
    updateChargePreview();
    if (!heldSlots.has('right-mouse')) return;
    attackCursor = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
  });
  const releaseAttack = (event?: Event): void => {
    if (event?.type === 'pointerup') {
      const button = (event as PointerEvent).button;
      if (button === 0) heldSlots.delete('left-mouse');
      if (button === 2) {
        heldSlots.delete('right-mouse');
        attackCursor = undefined;
      }
      return;
    }
    heldSlots.delete('left-mouse');
    heldSlots.delete('right-mouse');
    attackCursor = undefined;
  };
  const view = ctx.canvas.ownerDocument?.defaultView;
  view?.addEventListener('pointerup', releaseAttack, { capture: true });
  view?.addEventListener('pointercancel', releaseAttack, { capture: true });
  view?.addEventListener('blur', releaseAttack);
  view?.addEventListener('keydown', (event) => {
    if (event.code !== 'Escape' || !chargeTargeting) return;
    event.preventDefault();
    cancelChargeTargeting();
  });
  /** The three keyed slots, in the order their legends read on the bar. */
  const KEYED_SLOTS: Readonly<Record<string, ArpgAbilitySlot>> = {
    Digit3: 'key-3',
    Digit4: 'key-4',
    Digit5: 'key-5',
  };

  view?.addEventListener('keydown', (event) => {
    const slot = KEYED_SLOTS[event.code];
    if (!active || slot === undefined || ctx.state.chatInputFocused()) return;
    event.preventDefault();
    event.stopPropagation();
    const dashBound = dashSlot === slot;
    if (dashBound) {
      // A committed traversal toggles targeting rather than repeating: the aim
      // is the whole decision, so holding the key must not fire it twice.
      if (event.repeat) return;
      if (chargeTargeting) cancelChargeTargeting();
      else armChargeTargeting();
      return;
    }
    heldSlots.add(slot);
    castSlot(slot);
  });
  view?.addEventListener('keyup', (event) => {
    const slot = KEYED_SLOTS[event.code];
    if (slot === undefined) return;
    heldSlots.delete(slot);
    if (active) event.preventDefault();
  });
  view?.addEventListener('blur', () => {
    heldSlots.clear();
  });

  // Dev commands select by class name for convenience; the wire is keyed by
  // Soulstone (PRD #837), so resolve against the last roster the server sent.
  // An unresolvable class sends an empty id and the server answers with the
  // same roster error a missing stone always gets.
  const soulstoneFor = (classId: string): string =>
    knownSoulstones.find((stone: Soulstone) => stone.hero.classId === classId)?.id ??
    '';
  ctx.chatCommands.intercept((text) => {
    const trimmed = text.trim();
    const playerId = ctx.playerId();
    const combatLabMatch =
      /^\/dungeon\s+(?:combat|lab)(?:\s+(.*?))?\s*$/i.exec(trimmed);
    if (combatLabMatch) {
      // The lab takes a scenario, a class, or both, in either order. Three
      // classes means the tuning room has to be able to open on any of them.
      const args = (combatLabMatch[1] ?? '')
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter((token) => token.length > 0);
      const heroClassId = args.find((token) => isPlayableHeroClassId(token));
      const scenarioArg = args.find((token) => token !== heroClassId);
      if (
        scenarioArg !== undefined &&
        !COMBAT_LAB_SCENARIO_IDS.includes(
          scenarioArg as CombatLabScenarioId,
        )
      ) {
        showCombatLabHelp();
        return true;
      }
      hideCombatLabHelp();
      if (playerId !== undefined) {
        const labScenario = scenarioArg as CombatLabScenarioId | undefined;
        ctx.client()?.sendCommand({
          kind: 'StartDungeonRun',
          entity: playerId,
          soulstoneId: soulstoneFor(heroClassId ?? 'warrior'),
          mode: 'combat-lab',
          ...(labScenario !== undefined ? { labScenario } : {}),
        });
      }
      return true;
    }
    if (/^\/dungeon\s+(?:abandon|leave)\s*$/i.test(trimmed)) {
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'AbandonDungeonRun', entity: playerId });
      }
      return true;
    }
    // `/dungeon` walks through the gate to the Bone Knight Wall (PRD #837
    // slice 3): the class-and-seed instant procedural run retired with the
    // rest of the old gate flow, and the combat lab above stays the dev
    // entrance.
    if (/^\/dungeon\s*$/i.test(trimmed)) {
      if (playerId === undefined) return true;
      const gate = ctx.world()?.all().find(
        (entity) => entity.moongate?.purpose === 'dungeon',
      )?.id;
      if (gate === undefined) return true;
      ctx.client()?.sendCommand({
        kind: 'EnterMoongate',
        entity: playerId,
        gate,
      });
      return true;
    }
    return false;
  });

  ctx.events.on('connection', () => {
    blueprint = undefined;
    automap = undefined;
    runKey = '';
    wasInDungeon = false;
    active = false;
    bound = {};
    dashSlot = undefined;
    predictedBuffActionIds.clear();
    predictedBuffBySeq.clear();
    dashAvailable = false;
    localBusyUntilMs = 0;
    cancelChargeTargeting();
    heldSlots.clear();
    lastPointerAim = undefined;
    lastPointerCursor = undefined;
    releaseAttack();
    castSeq = 1;
    runGeneration = 0;
    ui.hide();
    combatHud.reset();
    ctx.state.setHudSuppressed(false);
  });

  ctx.onRendererBuilt((renderer) => {
    renderer.onSoulstoneKeeperUse = openSoulstoneDialog;
    renderer.onDungeonLootUse = (loot) => {
      const playerId = ctx.playerId();
      if (playerId === undefined) return;
      ctx.client()?.sendCommand({
        kind: 'PickupDungeonLoot',
        entity: playerId,
        loot,
      });
    };
    if (wasInDungeon) renderer.setDungeonLighting(false, { initialAmount: 1 });
    prepareBoundAbilities();
  });

  ctx.onFrame(() => {
    if (active && chargeTargeting) {
      updateChargePreview();
    }
    // A held slot re-casts the moment it is ready again, which is the hold-to-
    // swing feel the Warrior shipped with, now true of every slot and class.
    if (active) {
      for (const slot of heldSlots) {
        castSlot(
          slot,
          slot === 'right-mouse' && attackCursor ? attackCursor : undefined,
        );
      }
    }
    const world = ctx.world();
    const next = world?.map.toData().dungeon;
    const playerId = ctx.playerId();
    const position = playerId === undefined ? undefined : world?.get(playerId)?.position;
    const inDungeon = Boolean(next && position);
    const nextKey = next ? dungeonSourceKey(next) : '';
    if (nextKey) {
      ctx.renderer()?.setDungeonLighting(inDungeon, { sightRunKey: nextKey });
    } else {
      ctx.renderer()?.setDungeonLighting(inDungeon);
    }
    if (!next || !position) {
      if (automap) ui.hide();
      blueprint = undefined;
      automap = undefined;
      runKey = '';
      wasInDungeon = false;
      return;
    }
    wasInDungeon = true;
    if (!automap || nextKey !== runKey) {
      blueprint = next;
      automap = new DungeonAutomap(next);
      runKey = nextKey;
    }
    automap.enter({ x: position.x, y: position.y });
    ui.show(blueprint!, automap.snapshot());
  });
}
